import { Globe, RotateCw } from 'lucide-react'
import { useState } from 'react'
import { useNavigate } from 'react-router'
import { DataTable } from '@/components/ui/data-table'
import { DnsStatusIcon } from '@/components/ui/dns-status-icon'
import { LoadingPage } from '@/components/ui/loading'
import { MetricCard } from '@/components/ui/metric-card'
import { StatusBadge } from '@/components/ui/status-badge'
import { useDomains, useReverifyDomain } from '@/hooks/use-domains'
import { formatNumber } from '@/lib/format'
import type { DomainRecord } from '@/lib/types'

const PER_PAGE = 20

const STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: 'verified', label: 'Verified' },
  { value: 'pending', label: 'Pending' },
  { value: 'failed', label: 'Failed' },
] as const

export function DomainsPage(): React.JSX.Element {
  const navigate = useNavigate()
  const [page, setPage] = useState(1)
  const [statusFilter, setStatusFilter] = useState('')
  const [domainSearch, setDomainSearch] = useState('')
  const [accountSearch, setAccountSearch] = useState('')
  const [sortKey, setSortKey] = useState<string | undefined>(undefined)
  const [sortDir, setSortDir] = useState<string | undefined>(undefined)

  const { data, isLoading, isError } = useDomains({
    page,
    per_page: PER_PAGE,
    status: statusFilter || undefined,
    search: domainSearch || undefined,
    account: accountSearch || undefined,
    sort: sortKey,
    direction: sortDir,
  })

  const reverify = useReverifyDomain()

  const domains = data?.data ?? []
  const total = data?.total ?? 0

  const verifiedCount = domains.filter((d) => d.status === 'verified').length
  const pendingOrFailedCount = domains.filter(
    (d) => d.status === 'pending' || d.status === 'failed',
  ).length

  if (isLoading && domains.length === 0) {
    return <LoadingPage message="Loading domains..." />
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Globe className="h-6 w-6 text-admin-500" />
        <h1 className="text-2xl font-semibold text-text-primary">Domains</h1>
      </div>

      <DomainStats total={total} verified={verifiedCount} pendingOrFailed={pendingOrFailedCount} />

      <DomainFilters
        statusFilter={statusFilter}
        onStatusChange={(v) => {
          setStatusFilter(v)
          setPage(1)
        }}
        domainSearch={domainSearch}
        onDomainSearchChange={(v) => {
          setDomainSearch(v)
          setPage(1)
        }}
        accountSearch={accountSearch}
        onAccountSearchChange={(v) => {
          setAccountSearch(v)
          setPage(1)
        }}
      />

      {isError ? (
        <div className="rounded-xl border border-danger/30 bg-red-50 p-8 text-center">
          <p className="text-sm font-medium text-danger">Failed to load domains</p>
          <p className="mt-1 text-xs text-text-secondary">Check your connection and try again.</p>
        </div>
      ) : (
        <DataTable<DomainRecord>
          columns={buildColumns(reverify.mutate, navigate)}
          data={domains}
          pagination={{ page, perPage: PER_PAGE, total }}
          onPageChange={setPage}
          onSort={(key, direction) => {
            setSortKey(key)
            setSortDir(direction)
          }}
          emptyMessage="No domains match your filters"
        />
      )}
    </div>
  )
}

function DomainStats({
  total,
  verified,
  pendingOrFailed,
}: {
  total: number
  verified: number
  pendingOrFailed: number
}): React.JSX.Element {
  return (
    <div className="grid grid-cols-3 gap-4">
      <MetricCard label="Total Domains" value={formatNumber(total)} />
      <MetricCard label="Verified" value={formatNumber(verified)} variant="success" />
      <MetricCard
        label="Pending / Failed"
        value={formatNumber(pendingOrFailed)}
        variant={pendingOrFailed > 0 ? 'warning' : 'default'}
      />
    </div>
  )
}

function DomainFilters({
  statusFilter,
  onStatusChange,
  domainSearch,
  onDomainSearchChange,
  accountSearch,
  onAccountSearchChange,
}: {
  statusFilter: string
  onStatusChange: (v: string) => void
  domainSearch: string
  onDomainSearchChange: (v: string) => void
  accountSearch: string
  onAccountSearchChange: (v: string) => void
}): React.JSX.Element {
  return (
    <div className="flex flex-wrap items-center gap-3">
      <select
        value={statusFilter}
        onChange={(e) => onStatusChange(e.target.value)}
        className="rounded-lg border border-surface-3 bg-surface-0 px-3 py-2 text-sm text-text-primary focus:border-admin-500 focus:outline-none"
      >
        {STATUS_OPTIONS.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
      <input
        type="text"
        placeholder="Search domain..."
        value={domainSearch}
        onChange={(e) => onDomainSearchChange(e.target.value)}
        className="rounded-lg border border-surface-3 bg-surface-0 px-3 py-2 text-sm text-text-primary placeholder:text-text-tertiary focus:border-admin-500 focus:outline-none"
      />
      <input
        type="text"
        placeholder="Search account..."
        value={accountSearch}
        onChange={(e) => onAccountSearchChange(e.target.value)}
        className="rounded-lg border border-surface-3 bg-surface-0 px-3 py-2 text-sm text-text-primary placeholder:text-text-tertiary focus:border-admin-500 focus:outline-none"
      />
    </div>
  )
}

function buildColumns(
  onReverify: (id: string) => void,
  navigate: ReturnType<typeof useNavigate>,
): Parameters<typeof DataTable<DomainRecord>>[0]['columns'] {
  return [
    {
      key: 'domain',
      label: 'Domain',
      sortable: true,
      render: (row) => <span className="font-medium">{row.domain}</span>,
    },
    {
      key: 'account',
      label: 'Account',
      render: (row) => <span className="text-text-secondary text-xs">{row.account_email}</span>,
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      render: (row) => <StatusBadge status={row.status} />,
    },
    {
      key: 'spf',
      label: 'SPF',
      render: (row) => <DnsStatusIcon status={row.spf_status} />,
    },
    {
      key: 'dkim',
      label: 'DKIM',
      render: (row) => <DnsStatusIcon status={row.dkim_status} />,
    },
    {
      key: 'mx',
      label: 'MX',
      render: (row) => <DnsStatusIcon status={row.mx_status} />,
    },
    {
      key: 'dmarc',
      label: 'DMARC',
      render: (row) => <DnsStatusIcon status={row.dmarc_status} />,
    },
    {
      key: 'created_at',
      label: 'Created',
      render: (row) => (
        <span className="text-xs text-text-secondary">
          {new Date(row.created_at).toLocaleDateString()}
        </span>
      ),
    },
    {
      key: 'actions',
      label: '',
      render: (row) => (
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation()
              onReverify(row.id)
            }}
            className="inline-flex items-center gap-1 rounded-md border border-surface-3 px-2 py-1 text-xs text-text-secondary hover:bg-surface-1 hover:text-text-primary"
            title="Re-verify DNS"
          >
            <RotateCw className="h-3 w-3" />
            Re-verify
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation()
              void navigate(`/accounts/${row.account_id}`)
            }}
            className="rounded-md border border-surface-3 px-2 py-1 text-xs text-admin-600 hover:bg-admin-50"
          >
            View Account
          </button>
        </div>
      ),
    },
  ]
}
